import { ClientFacingErrors } from "../types/clientErrors";
import { GraphQLError } from "graphql";

export type DreamExtensions = {
  /**
   * Defaults to false. Set to true in implementation if the error should be logged to Sentry
   */
  shouldLogError?: boolean;
  /**
   * Only in production
   * Added in formatError in app.ts
   */
  eventId?: string;
  /**
   * Any info specifically meant for  Will not be sent to the frontend.
   * Stripped out in ApolloServer formatError option in app.ts.
   */
  debugInfo?: { isTest?: boolean; isAdmin?: boolean; [key: string]: unknown };
  /**
   * Error message override. Defaults to the ClientSideError code.
   * Useful for giving Sentry tickets a more descriptive title.
   */
  message?: string;
};

export class DreamError extends GraphQLError {
  declare extensions: DreamExtensions &
    GraphQLError["extensions"] & {
      /**
       * Code, guaranteed to be a ClientFacingError
       */
      code: ClientFacingErrors;
    };

  constructor(code: ClientFacingErrors, extensions?: DreamExtensions & GraphQLError["extensions"]) {
    super(extensions?.message ?? code, {
      extensions: {
        shouldLogError: false,
        code,
        ...extensions,
        isDreamError: true,
      },
    });
  }
}

export const isDreamError = (error: unknown): error is DreamError =>
  !!(error as { extensions?: Record<string, unknown> })?.extensions?.isDreamError;

export const formatDreamError = (error: DreamError) => {
  /**
   * If err.message !== err.extensions.code, then message has likely already
   * been overwritten somewhere to be more specific. Otherwise, use this
   * fallback logic for a more specific message.
   */
  if (error.message === error.extensions.code) {
    error.message = `${error.extensions.code}${
      /**
       * Try the specific name of the query we assigned
       */
      // @ts-expect-error `operation` could be non-existent
      error.extensions.debugInfo?.info?.operation?.name?.value
        ? // @ts-expect-error `operation` could be non-existent
          `: [query] ${error.extensions.debugInfo.info.operation.name.value}`
        : /**
           * Next try the name of the endpoint / root field
           */
          // @ts-expect-error `operation` could be non-existent
          error.extensions.debugInfo?.info?.path?.key
          ? // @ts-expect-error `operation` could be non-existent
            `: [field] ${error.extensions.debugInfo.info.path.key}`
          : /**
             * Fallback to the error path
             */
            error.path
            ? `: [path] ${error.path?.join(" > ")}`
            : ""
    }`;
  }
  return error;
};

export const shouldLogError = (err: Error | string | null | undefined) =>
  (err as DreamError)?.extensions?.shouldLogError !== false;
